// src/components/personal/sections/ClientsSection/components/Filters.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientFilters } from '../types/types';
import { getStatusOptions } from '../utils/statusHelpers';
import { TEXT_STYLES } from '@/theme/typography';
import { COMPONENT_STYLES } from '@/theme';
import { Card, Grid, Flex } from '@/components/ui/Layout';
import { Loader } from 'lucide-react';
import { getEmptyFilters } from '../utils/clientFilters';

interface FiltersProps {
  filters: ClientFilters;
  onChange: (filters: ClientFilters) => void;
  disabled?: boolean;
  isLoading?: boolean;
  onReset?: () => void;
  onRefresh?: () => void;
}

const Filters: React.FC<FiltersProps> = ({ 
  filters, 
  onChange,
  disabled = false,
  isLoading = false,
  onReset,
  onRefresh
}) => {
  const { t } = useTranslation(['clients', 'common']);
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Локальное состояние для полей формы
  const [localFilters, setLocalFilters] = useState<ClientFilters>(filters);
  const [isApplying, setIsApplying] = useState(false);
  
  // Обновляем локальные фильтры при изменении внешних
  useEffect(() => {
    if (!isApplying) {
      setLocalFilters(filters);
    }
  }, [filters, isApplying]);
  
  // Обработчик изменения фильтров
  const handleLocalChange = (field: keyof ClientFilters, value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Функция для создания точного совпадения
  const setExactMatch = (field: 'orders' | 'amount') => {
    if (field === 'orders' && localFilters.ordersFrom) {
      setLocalFilters(prev => ({
        ...prev,
        ordersTo: prev.ordersFrom
      }));
    } else if (field === 'amount' && localFilters.amountFrom) {
      setLocalFilters(prev => ({
        ...prev,
        amountTo: prev.amountFrom
      }));
    }
  };
  
  // Обработчик применения фильтров
  const handleApplyFilters = () => {
    if (DEBUG) console.log('Filters - Применение фильтров:', localFilters);
    
    setIsApplying(true);
    
    try {
      // Передаем фильтры в родительский компонент
      onChange(localFilters);
      
      // Вызываем функцию обновления данных после применения фильтров
      if (onRefresh) {
        console.log('Запуск обновления данных после применения фильтров');
        onRefresh();
      }
    } finally {
      // Добавляем задержку для визуального эффекта
      setTimeout(() => {
        setIsApplying(false);
      }, 500);
    }
  };
  
  // Обработчик сброса фильтров
  const handleReset = () => {
    if (DEBUG) console.log('Filters - Сброс фильтров');
    
    const resetFilters = getEmptyFilters();
    setLocalFilters(resetFilters);
    
    if (onReset) {
      onReset();
    } else {
      onChange(resetFilters);
    }
  };
  
  // Стили
  const inputClassName = `${COMPONENT_STYLES.input.base} ${(disabled || isLoading || isApplying) ? 'opacity-60 cursor-not-allowed' : ''}`;
  const labelClassName = `block ${TEXT_STYLES.small}`;
  const buttonBaseClass = `${COMPONENT_STYLES.button.base} text-sm px-4 py-1`;
  
  // Определяем, есть ли изменения в фильтрах
  const hasChanges = JSON.stringify(localFilters) !== JSON.stringify(filters);
  
  // Проверка активных фильтров
  const hasActiveFilters = Object.entries(localFilters).some(([key, value]) => {
    if (key === 'status') return value !== 'all';
    return value !== '' && value !== undefined && value !== null;
  });
  
  // Состояние блокировки UI
  const isBlocked = disabled || isLoading || isApplying;
  
  // Проверяем точное совпадение диапазонов
  const isExactRange = (from: string, to: string) => {
    return from && to && from === to;
  };
  
  // Получаем опции статусов
  const statusOptions = getStatusOptions(t);
  
  return (
    <Card padding="md" className="bg-background">
      <form onSubmit={(e) => { e.preventDefault(); handleApplyFilters(); }}>
        <Grid cols={3} gap="md">
          {/* Количество заказов */}
          <div className="space-y-2">
            <label className={labelClassName}>
              {t('filters.orders_count')}
            </label>
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder={t('filters.from')}
                  className={`${inputClassName} ${isExactRange(localFilters.ordersFrom, localFilters.ordersTo) ? 'border-primary' : ''}`}
                  value={localFilters.ordersFrom}
                  onChange={(e) => handleLocalChange('ordersFrom', e.target.value)}
                  disabled={isBlocked}
                  min="0"
                />
                <input
                  type="number"
                  placeholder={t('filters.to')}
                  className={`${inputClassName} ${isExactRange(localFilters.ordersFrom, localFilters.ordersTo) ? 'border-primary' : ''}`}
                  value={localFilters.ordersTo}
                  onChange={(e) => handleLocalChange('ordersTo', e.target.value)}
                  disabled={isBlocked}
                  min="0"
                />
              </div>
              
              {/* Кнопка точного совпадения */}
              {localFilters.ordersFrom && !isExactRange(localFilters.ordersFrom, localFilters.ordersTo) && (
                <button
                  type="button"
                  onClick={() => setExactMatch('orders')}
                  className="text-xs text-primary hover:underline self-start"
                  disabled={isBlocked}
                >
                  {t('filters.set_exact_match')}
                </button>
              )}
            
              {isExactRange(localFilters.ordersFrom, localFilters.ordersTo) && (
                <div className="text-xs text-primary mt-1">
                  {t('filters.exact_match')}
                </div>
              )}
            </div>
          </div>

          {/* Сумма заказов */}
          <div className="space-y-2">
            <label className={labelClassName}>
              {t('filters.order_amount')}
            </label>
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder={t('filters.from')}
                  className={`${inputClassName} ${isExactRange(localFilters.amountFrom, localFilters.amountTo) ? 'border-primary' : ''}`}
                  value={localFilters.amountFrom}
                  onChange={(e) => handleLocalChange('amountFrom', e.target.value)}
                  disabled={isBlocked}
                  min="0"
                  step="0.01"
                />
                <input
                  type="number"
                  placeholder={t('filters.to')}
                  className={`${inputClassName} ${isExactRange(localFilters.amountFrom, localFilters.amountTo) ? 'border-primary' : ''}`}
                  value={localFilters.amountTo}
                  onChange={(e) => handleLocalChange('amountTo', e.target.value)}
                  disabled={isBlocked}
                  min="0"
                  step="0.01"
                />
              </div>
              
              {/* Кнопка точного совпадения */}
              {localFilters.amountFrom && !isExactRange(localFilters.amountFrom, localFilters.amountTo) && (
                <button
                  type="button"
                  onClick={() => setExactMatch('amount')}
                  className="text-xs text-primary hover:underline self-start"
                  disabled={isBlocked}
                >
                  {t('filters.set_exact_match')}
                </button>
              )}
              
              {isExactRange(localFilters.amountFrom, localFilters.amountTo) && (
                <div className="text-xs text-primary mt-1">
                  {t('filters.exact_match')}
                </div>
              )}
            </div>
          </div>

          {/* Статус клиента */}
          <div className="space-y-2">
            <label className={labelClassName}>
              {t('filters.client_status')}
            </label>
            <select
              value={localFilters.status}
              onChange={(e) => handleLocalChange('status', e.target.value)}
              className={`${inputClassName} ${localFilters.status !== 'all' ? 'border-primary' : ''}`}
              disabled={isBlocked}
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </Grid>
        
        <Flex justify="end" gap="sm" className="mt-4">
          {/* Кнопка сброса */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleReset}
              className={`${buttonBaseClass} ${COMPONENT_STYLES.button.secondary}`}
              disabled={isBlocked}
            >
              {t('filters.reset')}
            </button>
          )}
          
          {/* Кнопка применения фильтров */}
          <button
            type="submit"
            className={`${buttonBaseClass} ${COMPONENT_STYLES.button.primary} ${isApplying ? 'animate-pulse' : ''}`}
            disabled={isBlocked || (!hasChanges && !isApplying)}
          >
            {isLoading || isApplying ? (
              <Flex align="center" gap="xs">
                <Loader className="animate-spin h-4 w-4" />
                {t('filters.applying')}
              </Flex>
            ) : (
              t('filters.apply')
            )}
          </button>
        </Flex>
      </form>
      
      {/* Индикатор загрузки */}
      {(isLoading || isApplying) && (
        <div className="mt-2 text-center text-xs text-secondary">
          <span className="inline-block mr-1">
            <svg className="animate-spin h-3 w-3 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          {t('filters.loading')}
        </div>
      )}
    </Card>
  );
};

export default React.memo(Filters);